/**
 * /Recon client auth context.
 *
 * Two-layer auth:
 *   - **Access token** lives in JS memory only (no localStorage) and rides
 *     the Authorization: Bearer header on every API call. Short-lived;
 *     a successful XSS attack would only get a near-expired token.
 *   - **Refresh token** lives in an HttpOnly + Secure (in HTTPS) +
 *     SameSite=Strict cookie set by the backend on /api/v1/client-auth.
 *     The JS bundle never sees it \u2014 set, read, and cleared exclusively
 *     by the browser \u2194 server. XSS-immune.
 *
 * On every page load the SPA calls POST /api/v1/client-auth/refresh,
 * which reads the cookie + mints a new access token. If the cookie is
 * present + valid the user stays logged in across refresh; if missing
 * or expired they're bounced to login. This replaces the old "memory
 * tokens only, refresh = log in again" UX while keeping the XSS
 * hardening posture.
 *
 * Auth flows wired here:
 *   - login(email, password)
 *   - signup(email, password, companyName?)
 *   - consumeSetupToken(token, password)  (post-signing first login)
 *   - consumeResetToken(token, password)
 *   - logout()
 *   - bootstrap on mount (silent /refresh)
 *
 * The auto-token Authorization header is wired through the same
 * @hackereyes/api-client interceptor pattern Operative uses.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  loginApiV1ClientAuthLoginPost,
  signupApiV1ClientAuthSignupPost,
  setupPasswordApiV1ClientAuthSetupSetupTokenPost,
  resetPasswordApiV1ClientAuthResetResetTokenPost,
  refreshApiV1ClientAuthRefreshPost,
  logoutApiV1ClientAuthLogoutPost,
  meApiV1ClientAuthMeGet,
} from "@hackereyes/api-client";
import { client } from "@hackereyes/api-client/client";

let _currentToken: string | null = null;

export function getClientAccessToken(): string | null {
  return _currentToken;
}

// One interceptor at module load \u2014 attaches Bearer header on every SDK
// request whenever we have a token. The SDK's `client` is shared with
// Operative; in /Recon's bundle only this interceptor runs.
client.interceptors.request.use((options) => {
  if (_currentToken) {
    options.headers.set("Authorization", `Bearer ${_currentToken}`);
  }
  return options;
});

interface AuthState {
  accessToken: string | null;
  clientId: string | null;
}

interface ClientProfile {
  client_id: string;
  display_name: string;
  contact_name: string;
  contact_email: string | null;
  company: string | null;
  email_verified: boolean;
}

interface AuthContextValue {
  accessToken: string | null;
  clientId: string | null;
  isAuthenticated: boolean;
  /** TRUE while the bootstrap /refresh call is in flight on initial mount.
   * Routes that gate on auth should render a neutral loading state during
   * this window so the user doesn't see a flash of "logged out" before the
   * cookie-driven refresh resolves. */
  isBootstrapping: boolean;
  /** The client's profile, fetched from /me after login/signup. NULL while
   * loading or before login. Used by the commission form to pre-fill
   * contact fields and by the verification gate. */
  me: ClientProfile | null;
  /** Re-fetch /me — call after the user verifies email, to flip the
   * email_verified flag in-context without forcing a logout/login cycle. */
  refreshMe: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, companyName?: string) => Promise<void>;
  consumeSetupToken: (token: string, password: string) => Promise<void>;
  consumeResetToken: (token: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const ClientAuthContext = createContext<AuthContextValue | null>(null);

export function ClientAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    accessToken: null,
    clientId: null,
  });
  const [me, setMe] = useState<ClientProfile | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState<boolean>(true);

  const fetchMe = useCallback(async (): Promise<ClientProfile | null> => {
    try {
      const { data } = await meApiV1ClientAuthMeGet();
      if (data) {
        const profile = data as unknown as ClientProfile;
        setMe(profile);
        return profile;
      }
    } catch {
      // Swallow: /me failure shouldn't block auth state.
    }
    return null;
  }, []);

  const applyTokens = useCallback(
    async (data: Record<string, unknown>) => {
      const accessToken = data.access_token as string | undefined;
      const clientId = data.client_id as string | undefined;
      if (!accessToken || !clientId) {
        throw new Error("malformed_token_response");
      }
      setState({ accessToken, clientId });
      _currentToken = accessToken;
      // Fetch /me right after tokens land so the commission form can
      // pre-fill and the verification gate can read email_verified.
      await fetchMe();
    },
    [fetchMe],
  );

  // Bootstrap on mount: call /refresh, which reads the HttpOnly cookie
  // (if present) and mints a fresh access token. This is what makes
  // page-refresh survive — the JS bundle had no tokens at startup; the
  // cookie does.
  //
  // The useRef guard prevents React 18 StrictMode's intentional double-
  // invocation of effects in dev from firing /refresh twice. The backend
  // enforces a linear rotation chain via a partial UNIQUE index on
  // parent_token_id (CLAUDE.md deviation #10), so two concurrent
  // refreshes with the same parent collide on the index. The ref
  // persists across StrictMode's mount → cleanup → remount cycle within
  // a page load; only a real page reload (which is when we want
  // bootstrap to re-run) resets it because the whole module
  // reinitializes.
  //
  // No `cancelled` flag here: with the ref guard, only one async runs
  // per page load, and React safely no-ops setState on an unmounted
  // instance. Adding the flag was over-defensive and accidentally made
  // setIsBootstrapping(false) skip when StrictMode's cleanup fired
  // between the request and its response — leaving the page stuck on
  // the Loading… state forever.
  const bootstrapFired = useRef<boolean>(false);
  useEffect(() => {
    if (bootstrapFired.current) return;
    bootstrapFired.current = true;
    (async () => {
      try {
        const { data, error } = await refreshApiV1ClientAuthRefreshPost();
        if (data && !error) {
          await applyTokens(data as unknown as Record<string, unknown>);
        }
      } catch {
        // No cookie / expired / network error — silent failure means the
        // user is treated as logged-out, which is the correct default for
        // a missing cookie. PortalShell will redirect to /portal/login.
      } finally {
        setIsBootstrapping(false);
      }
    })();
  }, [applyTokens]);

  const login = useCallback(
    async (email: string, password: string) => {
      const { data, error } = await loginApiV1ClientAuthLoginPost({
        body: { email, password } as never,
      });
      if (error || !data) {
        const detail = (error as Record<string, unknown>)?.detail as string | undefined;
        throw new Error(detail || "credentials_invalid");
      }
      applyTokens(data as Record<string, unknown>);
    },
    [applyTokens],
  );

  const signup = useCallback(
    async (email: string, password: string, companyName?: string) => {
      const { data, error } = await signupApiV1ClientAuthSignupPost({
        body: {
          email,
          password,
          company_name: companyName || null,
        } as never,
      });
      if (error || !data) {
        const detail = (error as Record<string, unknown>)?.detail as string | undefined;
        throw new Error(detail || "signup_failed");
      }
      applyTokens(data as Record<string, unknown>);
    },
    [applyTokens],
  );

  const consumeSetupToken = useCallback(
    async (token: string, password: string) => {
      const { data, error } = await setupPasswordApiV1ClientAuthSetupSetupTokenPost({
        path: { setup_token: token },
        body: { password } as never,
      });
      if (error || !data) {
        const detail = (error as Record<string, unknown>)?.detail as string | undefined;
        throw new Error(detail || "setup_token_invalid");
      }
      applyTokens(data as Record<string, unknown>);
    },
    [applyTokens],
  );

  const consumeResetToken = useCallback(
    async (token: string, password: string) => {
      const { error } = await resetPasswordApiV1ClientAuthResetResetTokenPost({
        path: { reset_token: token },
        body: { password } as never,
      });
      if (error) {
        const detail = (error as Record<string, unknown>)?.detail as string | undefined;
        throw new Error(detail || "reset_token_invalid");
      }
      // Reset doesn't auto-login \u2014 redirect to /portal/login afterwards.
    },
    [],
  );

  const logout = useCallback(async () => {
    // The refresh token lives in an HttpOnly cookie — the backend reads
    // and clears it. We just fire the call (it'll read the cookie via
    // credentials: include) and let server-side handle revocation.
    try {
      await logoutApiV1ClientAuthLogoutPost();
    } catch {
      // Logout failure shouldn't block local state cleanup.
    }
    setState({ accessToken: null, clientId: null });
    setMe(null);
    _currentToken = null;
  }, []);

  const refreshMe = useCallback(async () => {
    await fetchMe();
  }, [fetchMe]);

  const value = useMemo<AuthContextValue>(
    () => ({
      accessToken: state.accessToken,
      clientId: state.clientId,
      isAuthenticated: state.accessToken !== null,
      isBootstrapping,
      me,
      refreshMe,
      login,
      signup,
      consumeSetupToken,
      consumeResetToken,
      logout,
    }),
    [state.accessToken, state.clientId, isBootstrapping, me, refreshMe, login, signup, consumeSetupToken, consumeResetToken, logout],
  );

  return (
    <ClientAuthContext.Provider value={value}>
      {children}
    </ClientAuthContext.Provider>
  );
}

export function useClientAuth(): AuthContextValue {
  const ctx = useContext(ClientAuthContext);
  if (!ctx) {
    throw new Error("useClientAuth must be used within a ClientAuthProvider");
  }
  return ctx;
}
